import { invitedProjectsKey, ownedProjectsKey } from '@/hooks/keyUtils'
import { toaster } from '@/system/toaster'
import { isAdminUser } from '@/utils/user'
import { useAuth0 } from '@auth0/auth0-react'
import { useTranslation } from '@infraloop/i18n'
import {
  Project,
  ProjectRequestBody,
  deleteInvitedUserFromProject,
  deleteProject,
  getErrorMesssage,
  getInvitedProjects,
  getProjects,
  inviteUserToProject,
  patchProject,
  postProject,
} from '@infraloop/services'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router'

import useUser from './useUser'

/**
 * Loads the two project lists behind the dashboard tabs and owns the project mutations:
 * - **作成した工事** (owned) — `getProjects`, the projects the user created.
 * - **招待された工事** (invited) — `getInvitedProjects`, the projects shared with them.
 * - Create / update / delete a project, and invite / remove viewers, all updating the
 *   owned list's cache in place and toasting success or failure.
 *
 * The access token identifies the signed-in user. When an admin opens another user's
 * dashboard from the user list (`?user_id=`), that id is threaded through so the lists show
 * the target user's projects; the mutation cache is keyed to the same user.
 */
const useProjects = () => {
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const { user } = useUser()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  // Only admins may view another user's dashboard; ignore `user_id` for everyone else so a
  // crafted URL can't fire cross-user requests or write mutations into another user's cache.
  const targetUserId = isAdminUser(user) ? (searchParams.get('user_id') ?? undefined) : undefined

  const ownedQuery = useQuery<Project[]>({
    queryKey: ownedProjectsKey(targetUserId),
    staleTime: 60 * 1000,
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      return getProjects(accessToken, targetUserId)
    },
  })

  const invitedQuery = useQuery<Project[]>({
    queryKey: invitedProjectsKey(targetUserId),
    staleTime: 60 * 1000,
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      return getInvitedProjects(accessToken, targetUserId)
    },
  })

  /**
   * Create a new project and prepend it to the owned list — upserting by id, so a fetch
   * that already picked up the new project (a window-focus refetch racing the create)
   * can't leave it listed twice.
   */
  const createProjectMutation = useMutation({
    mutationFn: async ({ project }: { project: ProjectRequestBody }) => {
      const accessToken = await getAccessTokenSilently()
      return postProject(accessToken, project)
    },
    onSuccess: (created) => {
      queryClient.setQueryData<Project[]>(ownedProjectsKey(targetUserId), (prev = []) =>
        prev.some((p) => p.project_id === created.project_id)
          ? prev.map((p) => (p.project_id === created.project_id ? created : p))
          : [created, ...prev],
      )
      toaster.success({ description: t('hatsuly.toast.project.created') })
    },
    onError: (error) => {
      toaster.error({ description: getErrorMesssage(error, t) })
    },
  })

  /**
   * Update an existing project (name and default construction properties).
   */
  const updateProjectMutation = useMutation({
    mutationFn: async ({ projectId, project }: { projectId: string; project: ProjectRequestBody }) => {
      const accessToken = await getAccessTokenSilently()
      return patchProject(accessToken, projectId, project)
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<Project[]>(ownedProjectsKey(targetUserId), (prev = []) =>
        prev.map((p) => (p.project_id === updated.project_id ? updated : p)),
      )
      toaster.success({ description: t('hatsuly.toast.project.updated') })
    },
    onError: (error) => {
      toaster.error({ description: getErrorMesssage(error, t) })
    },
  })

  /**
   * Delete a project and drop it from the owned list.
   */
  const deleteProjectMutation = useMutation({
    mutationFn: async ({ projectId }: { projectId: string }) => {
      const accessToken = await getAccessTokenSilently()
      await deleteProject(accessToken, projectId)
      return projectId
    },
    onSuccess: (projectId) => {
      queryClient.setQueryData<Project[]>(ownedProjectsKey(targetUserId), (prev = []) =>
        prev.filter((p) => p.project_id !== projectId),
      )
      toaster.success({ description: t('hatsuly.toast.project.deleted') })
    },
    onError: (error) => {
      toaster.error({ description: getErrorMesssage(error, t) })
    },
  })

  /**
   * Invite a viewer to a project by email; the API returns the project with its
   * updated viewer list. No error toast here — the viewers modal shows invite
   * failures inline next to the form, where they're easier to notice.
   */
  const inviteViewerMutation = useMutation({
    mutationFn: async ({ projectId, email }: { projectId: string; email: string }) => {
      const accessToken = await getAccessTokenSilently()
      return inviteUserToProject(accessToken, projectId, email)
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<Project[]>(ownedProjectsKey(targetUserId), (prev = []) =>
        prev.map((p) => (p.project_id === updated.project_id ? updated : p)),
      )
      toaster.success({ description: t('hatsuly.toast.viewers.invited') })
    },
  })

  /**
   * Remove an invited viewer from a project.
   */
  const removeViewerMutation = useMutation({
    mutationFn: async ({ projectId, userId }: { projectId: string; userId: string }) => {
      const accessToken = await getAccessTokenSilently()
      await deleteInvitedUserFromProject(accessToken, projectId, userId)
      return { projectId, userId }
    },
    onSuccess: ({ projectId, userId }) => {
      queryClient.setQueryData<Project[]>(ownedProjectsKey(targetUserId), (prev = []) =>
        prev.map((p) => {
          if (p.project_id !== projectId) return p
          const sharedUser = { ...p.shared_user }
          delete sharedUser[userId]
          return { ...p, shared_user: sharedUser }
        }),
      )
      toaster.success({ description: t('hatsuly.toast.viewers.removed') })
    },
    onError: (error) => {
      toaster.error({ description: getErrorMesssage(error, t) })
    },
  })

  return {
    ownedProjects: ownedQuery.data ?? [],
    invitedProjects: invitedQuery.data ?? [],
    isLoadingOwned: ownedQuery.isLoading,
    isLoadingInvited: invitedQuery.isLoading,

    createProject: createProjectMutation.mutateAsync,
    isCreatingProject: createProjectMutation.isPending,

    updateProject: updateProjectMutation.mutateAsync,
    isUpdatingProject: updateProjectMutation.isPending,

    deleteProject: deleteProjectMutation.mutateAsync,
    isDeletingProject: deleteProjectMutation.isPending,

    inviteViewer: inviteViewerMutation.mutateAsync,
    isInvitingViewer: inviteViewerMutation.isPending,

    removeViewer: removeViewerMutation.mutateAsync,
    isRemovingViewer: removeViewerMutation.isPending,
  }
}

export default useProjects
